import React, { useState, useEffect } from 'react';
import PortalLayout from '../../components/Portal/PortalLayout';
import { portalTournamentAPI } from '../../services/api';

/**
 * PortalMessages - Director messages and announcements
 * Shows important messages from tournament directors across all tournaments
 */
const PortalMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, important

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        // Get all tournaments for the member
        const tournamentsRes = await portalTournamentAPI.getMyTournaments();
        const tournaments = tournamentsRes.data?.data?.tournaments || [];

        // Collect all activity posts from tournaments
        const allPosts = [];
        for (const { tournament } of tournaments) {
          if (tournament?._id) {
            try {
              const activityRes = await portalTournamentAPI.getActivity(tournament._id);
              const posts = activityRes.data?.data?.posts || [];

              // Add tournament context to each post
              posts.forEach(post => {
                allPosts.push({
                  ...post,
                  tournamentName: tournament.name,
                  tournamentDate: tournament.date,
                  tournamentId: tournament._id
                });
              });
            } catch (err) {
              console.error(`Failed to fetch activity for tournament ${tournament._id}:`, err);
            }
          }
        }

        // Sort by date, newest first
        allPosts.sort((a, b) => new Date(b.publishedAt || b.createdAt) - new Date(a.publishedAt || a.createdAt));

        setMessages(allPosts);
      } catch (err) {
        console.error('Failed to load messages:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getMessageIcon = (pinned) => {
    if (pinned) {
      return (
        <svg className="h-5 w-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    );
  };

  const filteredMessages = messages.filter(msg => {
    if (filter === 'important') return msg.pinned;
    if (filter === 'unread') return false; // Would need to track read status
    return true;
  });

  if (loading) {
    return (
      <PortalLayout title="Messages">
        <div className="flex justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Messages">
      <div className="space-y-4">
        {/* Filter Tabs */}
        <div className="flex gap-2 border-b border-gray-200">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === 'all'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            All ({messages.length})
          </button>
          <button
            onClick={() => setFilter('important')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              filter === 'important'
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Important ({messages.filter(m => m.pinned).length})
          </button>
        </div>

        {/* Messages List */}
        {filteredMessages.length > 0 ? (
          <div className="space-y-3">
            {filteredMessages.map((message) => (
              <div
                key={message._id}
                className={`bg-white rounded-xl p-4 shadow-sm border ${
                  message.pinned ? 'border-yellow-300 bg-yellow-50' : 'border-gray-100'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    {getMessageIcon(message.pinned)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className="font-medium text-gray-900 text-sm">
                        {message.title || 'Announcement'}
                      </h3>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {formatDate(message.publishedAt || message.createdAt)}
                      </span>
                    </div>

                    <p className="text-sm text-gray-600 mb-2 whitespace-pre-wrap">
                      {message.content}
                    </p>

                    <div className="flex items-center gap-2 text-xs">
                      <span className="inline-flex items-center gap-1 text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" clipRule="evenodd" />
                        </svg>
                        {message.tournamentName}
                      </span>
                      {message.tournamentDate && (
                        <span className="text-gray-500">
                          {new Date(message.tournamentDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="text-4xl mb-2">📬</div>
            <h3 className="font-medium text-gray-700 mb-1">No Messages</h3>
            <p className="text-sm text-gray-500">
              {filter === 'important'
                ? 'No important messages at this time'
                : 'You have no messages from tournament directors'}
            </p>
          </div>
        )}
      </div>
    </PortalLayout>
  );
};

export default PortalMessages;
